/**
 * @file auth.ts
 * @author Nikitha Anpur
 * @description Authentication utility functions for the Flow Builder application.
 *              Contains methods for token management and authentication.
 * @created June 5, 2024
 */
import { getEnvVariable } from "../../../envs";
/**
 * Gets the application token based on the access portal type.
 * @description Retrieves the appropriate token from localStorage based on whether
 *              the access portal is internal ('I') or external. Returns internal token
 *              for internal portals and external token for all other portal types.
 * @returns {string | null} The stored application token or null if not found
 */
export const getApplicationToken = () => {
    const accessPortal = localStorage.getItem('AccessPortal') || 'I'; // Default to 'I' if not set
    const tokenPrefix = accessPortal === 'I' ? 'internaltoken' : 'externaltoken';
    const moduleKey = String(getEnvVariable('ModuleKey') || getEnvVariable('AppModuleKey') || '');
    const titleKeyRaw = String(getEnvVariable('TitleKey') || getEnvVariable('Title') || '');
    const normalize = (s) => s ? s.replace(/\s+/g, '_').replace(/[^A-Za-z0-9_]/g, '') : '';
    const titleKey = normalize(titleKeyRaw);
    const candidates = [];
    // 
    candidates.push(tokenPrefix);
    if (moduleKey && titleKey)
        candidates.push(`${tokenPrefix}_${moduleKey}_${titleKey}`);
    // 
    for (const key of candidates) {
        const v = localStorage.getItem(key);
        if (v)
            return v;
    }
    // Fallback: the token key may carry a dynamic suffix (e.g. `internaltoken_APPS_ONESTOP`) that the
    // env-derived candidates don't reproduce. Match any key that STARTS WITH the prefix instead.
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(tokenPrefix)) {
                const v = localStorage.getItem(key);
                if (v)
                    return v;
            }
        }
    }
    catch {
        /* ignore */
    }
    return null;
};
